import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CareerLaunchSection from "../components/about";
import image from "../asserts/institute5.avif";

import {
  IconArrowUpRight,
  IconSchool,
  IconCode,
  IconSpeakerphone,
  IconDeviceMobile,
  IconCheck,
} from "@tabler/icons-react";

import Hero from "../components/Hero";
import Footer from "../components/Footer";


/* =========================================================
   ANIMATION SETTINGS
========================================================= */

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeLeft = {
  hidden: {
    opacity: 0,
    x: -60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeRight = {
  hidden: {
    opacity: 0,
    x: 60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const cardAnimation = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const statAnimation = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};


/* =========================================================
   HOME
========================================================= */

export default function Home() {
  useEffect(() => {
    document.title =
      "WE3X – IT Training, Software & Digital Marketing";

    let metaDescription = document.querySelector(
      'meta[name="description"]'
    );

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }

    metaDescription.content =
      "WE3X offers IT training, software development, web and app development, SEO, Google Ads and digital marketing services in Tamil Nadu.";
  }, []);

  return (
    <main className="overflow-hidden bg-white text-neutral-950">

      {/* =====================================================
          SECTION 1 — HERO
      ===================================================== */}

      <Hero />


      {/* =====================================================
          SECTION 2 — IMAGE + CONTENT
      ===================================================== */}

      <section className="relative bg-white py-20 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">

          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">

            {/* IMAGE — ENTERS FROM LEFT */}

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.25,
              }}
              className="relative"
            >
              <div
                className="
                  relative
                  overflow-hidden
                  rounded-[28px]
                  border
                  border-neutral-200
                  bg-neutral-100
                "
              >
                <motion.img
                  src={image}
                  alt="WE3X Technologies team working on software development and digital marketing projects"
                  className="
                    h-[300px]
                    w-full
                    object-cover
                    sm:h-[360px]
                    lg:h-[420px]
                  "
                  initial={{
                    scale: 1.08,
                  }}
                  whileInView={{
                    scale: 1,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 1.2,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />
              </div>
            </motion.div>


            {/* CONTENT — ENTERS FROM RIGHT */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.25,
              }}
            >
              <motion.div
                variants={fadeUp}
                className="
                  mb-5
                  inline-flex
                  items-center
                  gap-2
                  text-xs
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  text-neutral-500
                "
              >
                <span className="h-[1px] w-8 bg-neutral-300" />

                About WE3X
              </motion.div>


              <motion.h2
                variants={fadeUp}
                className="
                  merriweather
                  max-w-xl
                  text-3xl
                  font-black
                  tracking-[-0.04em]
                  text-neutral-950
                  sm:text-4xl
                  lg:text-[48px]
                  lg:leading-[1.08]
                "
              >
                Technology, learning and digital growth.
              </motion.h2>


              <motion.p
                variants={fadeUp}
                className="
                  mt-6
                  max-w-xl
                  text-[16px]
                  leading-7
                  text-neutral-600
                "
              >
                WE3X brings together professional IT education,
                custom software development and digital marketing
                under one technology-driven brand.
              </motion.p>


              <motion.p
                variants={fadeUp}
                className="
                  mt-4
                  max-w-xl
                  text-[16px]
                  leading-7
                  text-neutral-600
                "
              >
                Our institute helps students build practical,
                industry-focused skills, while WE3X Technologies
                helps startups and businesses build digital products,
                automate operations and improve their online visibility.
              </motion.p>


              {/* FEATURES */}

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                  amount: 0.3,
                }}
                className="mt-8 grid gap-4 sm:grid-cols-2"
              >
                <Feature text="Industry-focused IT training" />

                <Feature text="Custom software development" />

                <Feature text="SEO & digital marketing" />

                <Feature text="Web & mobile applications" />
              </motion.div>


              {/* BUTTONS */}

              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                }}
                className="mt-9 flex flex-col gap-3 sm:flex-row"
              >
                <Link
                  to="/institute"
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-full
                    bg-neutral-950
                    px-6
                    py-3.5
                    text-sm
                    font-bold
                    text-white
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:bg-neutral-800
                  "
                >
                  Explore WE3X Institute

                  <IconArrowUpRight size={17} />
                </Link>

                <Link
                  to="/technology"
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    rounded-full
                    border
                    border-neutral-300
                    bg-white
                    px-6
                    py-3.5
                    text-sm
                    font-bold
                    text-neutral-950
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:border-neutral-950
                  "
                >
                  Explore WE3X Technologies

                  <IconArrowUpRight size={17} />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 3 — SERVICES
      ===================================================== */}

      <section className="bg-neutral-50 py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">

          {/* SECTION HEADER */}

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            className="mb-12 max-w-3xl sm:mb-16"
          >
            <div
              className="
                mb-4
                text-xs
                font-bold
                uppercase
                tracking-[0.2em]
                text-neutral-500
              "
            >
              What We Do
            </div>

            <h2
              className="
                merriweather
                text-3xl
                font-black
                tracking-[-0.04em]
                text-neutral-950
                sm:text-4xl
                lg:text-[48px]
                lg:leading-[1.08]
              "
            >
              Skills for careers.
              <br />
              Technology for businesses.
            </h2>

            <p
              className="
                mt-5
                max-w-2xl
                text-[16px]
                leading-7
                text-neutral-600
              "
            >
              From professional IT training to custom software and
              performance-driven digital marketing, WE3X provides
              practical solutions for students, startups and growing
              businesses.
            </p>
          </motion.div>


          {/* CARDS — STAGGERED SCROLL ANIMATION */}

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.12,
            }}
            className="grid gap-5 md:grid-cols-2"
          >
            <ServiceCard
              icon={<IconSchool size={25} stroke={1.8} />}
              eyebrow="WE3X Institute"
              title="Professional IT Training"
              description="Learn in-demand technologies through practical, project-based training designed around current industry requirements."
              items={[
                "MERN Stack Development",
                "Digital Marketing",
                "UI/UX & Graphic Design",
                "Cyber Security",
                "Python full Stack Development",
                "Tally Prime",
                "Basic Computer Skills",
                "Data analytics",
              ]}
              link="/institute"
              linkText="Explore IT Training"
            />

            <ServiceCard
              icon={<IconCode size={25} stroke={1.8} />}
              eyebrow="Software Development"
              title="Custom Software Development for Businesses"
              description="Build scalable digital solutions tailored to your business processes, customers and day-to-day operational requirements."
              items={[
                "Billing Software",
                "POS Systems",
                "CRM Software",
                "Business Automation",
              ]}
              link="/technology"
              linkText="Explore Custom Software Development"
            />

            <ServiceCard
              icon={<IconSpeakerphone size={25} stroke={1.8} />}
              eyebrow="Business Growth"
              title="Digital Marketing Services"
              description="Improve visibility, generate qualified leads and grow your brand using measurable digital marketing strategies."
              items={[
                "SEO Services",
                "Google Ads",
                "Meta Ads",
                "Social Media Marketing",
              ]}
              link="/digital-marketing"
              linkText="Explore Digital Marketing Services"
            />

            <ServiceCard
              icon={<IconDeviceMobile size={25} stroke={1.8} />}
              eyebrow="Digital Products"
              title="Web & Mobile App Development"
              description="Create responsive websites, business web applications and mobile experiences designed around your users and business goals."
              items={[
                "Business Websites",
                "Web Applications",
                "Mobile Applications",
                "Landing Pages",
              ]}
              link="/technology"
              linkText="Explore Web & App Development"
            />
          </motion.div>
        </div>
      </section>


      {/* =====================================================
          SECTION 4 — STATISTICS
      ===================================================== */}

      {/* <section className="merriweather border-y border-blue-100 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            className="
              grid
              grid-cols-2
              divide-x
              divide-y
              divide-blue-100
              md:grid-cols-4
              md:divide-y-0
            "
          >
            <StatItem
              number="5+"
              label="Students Trained"
            />

            <StatItem
              number="2+"
              label="Projects Delivered"
            />

            <StatItem
              number="3+"
              label="Corporate Clients"
            />

            <StatItem
              number="98%"
              label="Client Satisfaction"
            />
          </motion.div>
        </div>
      </section> */}
      <CareerLaunchSection />
      <Footer />

    </main>
  );
}


/* =========================================================
   FEATURE
========================================================= */

function Feature({ text }) {
  return (
    <motion.div
      variants={fadeUp}
      className="flex items-center gap-3"
    >
      <motion.div
        whileHover={{
          scale: 1.12,
        }}
        className="
          flex
          h-7
          w-7
          shrink-0
          items-center
          justify-center
          rounded-full
          bg-neutral-100
        "
      >
        <IconCheck size={15} stroke={2.2} />
      </motion.div>

      <span className="text-sm font-semibold text-neutral-700">
        {text}
      </span>
    </motion.div>
  );
}


/* =========================================================
   SERVICE CARD
========================================================= */

function ServiceCard({
  icon,
  eyebrow,
  title,
  description,
  items,
  link,
  linkText,
}) {
  return (
    <motion.article
      variants={cardAnimation}
      whileHover={{
        y: -6,
      }}
      className="
        group
        relative
        overflow-hidden
        rounded-[24px]
        border
        border-neutral-200
        bg-white
        p-6
        transition-shadow
        duration-300
        hover:border-neutral-300
        hover:shadow-xl
        hover:shadow-black/[0.05]
        sm:p-8
        lg:p-9
      "
    >
      {/* ICON */}

      <motion.div
        whileHover={{
          rotate: 4,
          scale: 1.08,
        }}
        className="
          mb-8
          flex
          h-12
          w-12
          items-center
          justify-center
          rounded-xl
          border
          border-neutral-200
          bg-neutral-50
          text-neutral-950
          transition-colors
          duration-300
          group-hover:bg-neutral-950
          group-hover:text-white
        "
      >
        {icon}
      </motion.div>


      <div
        className="
          text-[11px]
          font-bold
          uppercase
          tracking-[0.16em]
          text-neutral-400
        "
      >
        {eyebrow}
      </div>


      <h3
        className="
          merriweather
          mt-3
          max-w-md
          text-[22px]
          font-black
          leading-[1.2]
          tracking-[-0.025em]
          text-neutral-950
          sm:text-2xl
        "
      >
        {title}
      </h3>


      <p className="mt-4 max-w-lg text-sm leading-6 text-neutral-600">
        {description}
      </p>


      {/* ITEMS */}

      <div className="mt-7 grid gap-2 sm:grid-cols-2">
        {items.map((item, index) => (
          <motion.div
            key={item}
            initial={{
              opacity: 0,
              y: 10,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              delay: index * 0.06,
              duration: 0.4,
            }}
            className="
              flex
              items-center
              gap-2
              rounded-lg
              bg-neutral-50
              px-3
              py-2.5
              text-xs
              font-semibold
              text-neutral-700
            "
          >
            <span className="h-1.5 w-1.5 rounded-full bg-neutral-950" />

            {item}
          </motion.div>
        ))}
      </div>


      <Link
        to={link}
        className="
          mt-8
          inline-flex
          items-center
          gap-2
          text-sm
          font-bold
          text-neutral-950
        "
      >
        {linkText}

        <IconArrowUpRight
          size={17}
          className="
            transition-transform
            duration-300
            group-hover:translate-x-1
            group-hover:-translate-y-1
          "
        />
      </Link>
    </motion.article>
  );
}


/* =========================================================
   STAT ITEM
========================================================= */

function StatItem({ number, label }) {
  return (
    <motion.div
      variants={statAnimation}
      className="
        flex
        flex-col
        items-center
        justify-center
        px-4
        py-12
        text-center
        sm:py-16
        lg:py-20
      "
    >
      <motion.h3
        initial={{
          scale: 0.8,
        }}
        whileInView={{
          scale: 1,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="
          text-5xl
          font-medium
          tracking-tight
          text-blue-600
          sm:text-6xl
          lg:text-7xl
        "
      >
        {number}
      </motion.h3>

      <p
        className="
          mt-5
          text-xs
          font-semibold
          uppercase
          tracking-[0.12em]
          text-neutral-600
          sm:text-sm
        "
      >
        {label}
      </p>
    </motion.div>
  );
}